
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, Clock, User, Timer, LogOut } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

import { usePinAuth } from '@/hooks/usePinAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { useTimeStore } from '@/store/timeStore';
import { useTimeEntries } from '@/hooks/useTimeEntries';

import { formatHours } from '@/utils/timeUtils';
import ActiveTimeTracker from '../TimeTracker/ActiveTimeTracker';
import { OfflineIndicator } from '../OfflineSupport/OfflineIndicator';
import { TodayShiftsSummary } from './TodayShiftsSummary';
import { ActiveCrewDisplay } from '../PixelCharacter/ActiveCrewDisplay';
import { EquipmentAlertBanner } from '@/components/Equipment/EquipmentAlertBanner';

export const RealTimeDashboard: React.FC = React.memo(() => {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const { signOut: pinSignOut, pinUser } = usePinAuth();
  const { isManager } = useUserRole();
  const navigate = useNavigate();
  // @ts-expect-error - stats est ajouté dynamiquement dans le store
  const { activeSession, stats } = useTimeStore();
  const { stats: queryStats } = useTimeEntries(currentUser?.id);

  // Récupérer l'utilisateur connecté
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        // Vérifier d'abord l'authentification PIN
        if (pinUser) {
          setCurrentUser({
            id: pinUser.id,
            email: pinUser.email,
            user_metadata: {
              first_name: pinUser.name.split(' ')[0],
              last_name: pinUser.name.split(' ')[1] || ''
            }
          });
          setLoading(false);
          return;
        }


      } catch (error) {
        console.error('❌ Erreur auth dashboard:', error);
      } finally {
        setLoading(false);
      }
    };
    getCurrentUser();
  }, [pinUser]);

  // Memoization des statistiques
  const dashboardStats = useMemo(() => {
    const source = queryStats || stats;
    const todayHours = source?.todayHours || 0;
    const weeklyHours = source?.thisWeekHours || 0;

    return {
      todayTotal: todayHours,
      weeklyTotal: weeklyHours,
      totalEntries: source?.totalEntries || 0,
      totalHours: source?.totalHours || 0,
    };
  }, [stats, queryStats]);

  // Gestionnaire de navigation optimisé
  const handleNavigation = useCallback((path: string) => {
    // Logique de navigation si nécessaire
    console.log('Navigation vers:', path);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
        <div className="max-w-md mx-auto">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>Chargement du tableau de bord...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
        <div className="max-w-md mx-auto text-center">
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-bold mb-4">Connexion Requise</h2>
              <p className="text-gray-600 mb-4">
                Vous devez être connecté pour accéder au suivi de temps.
              </p>
              <Link to="/login">
                <Button>Se Connecter</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
      <OfflineIndicator />
      
      <div className="max-w-md mx-auto space-y-6">
        {/* Header avec Logo */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="/lovable-uploads/dd4163e4-84e9-4377-80b0-60d39fb251a9.png" 
              alt="Caribou - Peinture & Rénovations Sensées" 
              className="h-20 w-auto"
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Time Tracker</h1>
          <p className="text-muted-foreground">Suivi de temps - Construction</p>
        </div>

        {/* Alertes équipements — managers et admins */}
        {isManager() && <EquipmentAlertBanner />}

        {/* Chantier en cours - employés actifs */}
        <ActiveCrewDisplay />

        {/* Navigation rapide */}
        <div className="grid grid-cols-2 gap-3">
          <Link to="/weekly">
            <Button
              variant="outline"
              className="w-full h-16 flex flex-col gap-2"
              onClick={() => handleNavigation('/weekly')}
            >
              <Calendar className="h-5 w-5" />
              <span className="text-sm">Ma semaine</span>
            </Button>
          </Link>
          <Link to="/profile">
            <Button
              variant="outline"
              className="w-full h-16 flex flex-col gap-2"
              onClick={() => handleNavigation('/profile')}
            >
              <User className="h-5 w-5" />
              <span className="text-sm">Mon profil</span>
            </Button>
          </Link>
        </div>

{isManager() && (
        <div className="grid grid-cols-2 gap-3">
          <Link to="/manager">
            <Button 
              variant="outline" 
              className="w-full h-16 flex flex-col gap-2"
              onClick={() => handleNavigation('/manager')}
            >
              <Clock className="h-5 w-5" />
              <span className="text-sm">Manager</span>
            </Button>
          </Link>
          <Button variant="outline" className="w-full h-16 flex flex-col gap-2" disabled>
            <Timer className="h-5 w-5" />
            <span className="text-sm">Rapports</span>
          </Button>
        </div>
        )}

        {/* Suivi de temps actif */}
        <ActiveTimeTracker />

        {/* Résumé des shifts d'aujourd'hui */}
        <TodayShiftsSummary userId={currentUser?.id} />

        {/* Stats réelles */}
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">
                {formatHours(dashboardStats.todayTotal * 60)}
              </div>
              <div className="text-sm text-muted-foreground">Aujourd'hui</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">
                {formatHours(dashboardStats.weeklyTotal * 60)}
              </div>
              <div className="text-sm text-muted-foreground">Cette semaine</div>
            </CardContent>
          </Card>
        </div>



        {/* Informations utilisateur */}
        <div className="text-xs text-center text-gray-500 bg-white p-3 rounded border">
          <div><strong>👤 Utilisateur connecté:</strong></div>
          <div>{currentUser.email}</div>
          <div className="mt-1">
            <strong>📊 Données:</strong> {dashboardStats.totalEntries} entrées de temps
          </div>
          {activeSession && (
            <div className="mt-1 text-green-600">
              <strong>⏱️ Session active</strong>
            </div>
          )}

          <Button
            variant="outline"
            onClick={() => { 
              pinSignOut();
              navigate('/login', { replace: true });
            }}
            className="mt-3 flex items-center gap-2 mx-auto text-xs"
          >
            <LogOut className="h-4 w-4" />
            <span>Déconnexion</span>
          </Button>
        </div>

      </div>
    </div>
  );
});

RealTimeDashboard.displayName = 'RealTimeDashboard';
